import styled, { css } from 'styled-components';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import * as CollapsiblePrimitive from '@radix-ui/react-collapsible';
import type { EndpointMethod } from './types';

export const ReferenceCard = styled.section`
    overflow: hidden;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-xl, 14px);
    background: var(--color-card);
    box-shadow: var(--shadow-sm);
`;
export const ReferenceHeader = styled.header`
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: var(--space-3, 12px);
    min-height: 56px;
    padding: var(--space-3, 12px) var(--space-4, 16px);
    border-bottom: 1px solid var(--color-border);
`;
export const ReferenceTitle = styled.h3`
    margin: 0;
    font-size: var(--font-size-sm, 13px);
    font-weight: var(--font-weight-semibold, 600);
`;
export const ReferenceControls = styled.div`
    display: flex;
    min-width: 0;
    align-items: center;
    justify-content: flex-end;
    flex: 1;
    flex-wrap: wrap;
    gap: var(--space-2, 8px);
    @media (max-width: 640px) {
        flex-basis: 100%;
        justify-content: flex-start;
    }
`;
export const ReferenceEndpointCount = styled.span`
    color: var(--color-muted-foreground);
    font-size: var(--font-size-xs, 11px);
    white-space: nowrap;
`;
export const ReferenceSearchField = styled.label`
    position: relative;
    display: flex;
    width: min(240px, 100%);
    align-items: center;

    svg {
        position: absolute;
        left: var(--space-3, 12px);
        width: 15px;
        height: 15px;
        color: var(--color-muted-foreground);
        pointer-events: none;
    }

    @media (max-width: 640px) {
        width: 100%;
        order: 3;
    }
`;
export const ReferenceSearchInput = styled.input`
    width: 100%;
    height: 36px;
    padding: 0 var(--space-3, 12px) 0 36px;
    border: 1px solid var(--color-border);
    border-radius: 9999px;
    color: var(--color-foreground);
    background: var(--color-background);
    font-size: var(--font-size-sm, 12px);

    &:focus {
        border-color: var(--color-ring);
        outline: 2px solid color-mix(in srgb, var(--color-ring) 24%, transparent);
    }
`;
export const ReferenceDashedFilter = styled.div`
    button {
        height: 36px;
        border: 1.5px dashed var(--color-border, #d1d5db);
        border-radius: 9999px;
        background-image: none;
    }

    button:hover {
        border-color: var(--color-border-hover, #9ca3af);
    }
`;
export const ReferenceDashedFilterLabel = styled.span`
    color: var(--color-muted-foreground);
`;
export const ReferenceEntityFilterCount = styled.span`
    margin-left: auto;
    color: var(--color-muted-foreground);
    font-size: var(--font-size-xs, 11px);
`;
export const ReferenceEmptyState = styled.p`
    margin: 0;
    padding: var(--space-8, 32px) var(--space-4, 16px);
    color: var(--color-muted-foreground);
    font-size: var(--font-size-sm, 12px);
    text-align: center;
`;
export const EndpointEntitySection = styled.section`
    & + & {
        border-top: 1px solid var(--color-border);
    }
`;
export const EndpointEntityHeader = styled.header`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--space-2, 8px) var(--space-4, 16px);
    border-bottom: 1px solid var(--color-border);
    background: var(--color-muted);
`;
export const EndpointEntityTitle = styled.h4`
    margin: 0;
    font-size: var(--font-size-xs, 11px);
    font-weight: var(--font-weight-semibold, 600);
`;
export const EndpointEntityCount = styled.span`
    color: var(--color-muted-foreground);
    font-size: var(--font-size-xs, 10px);
`;
export const EndpointList = styled(AccordionPrimitive.Root)``;
export const OperationItem = styled(AccordionPrimitive.Item)`
    border-bottom: 1px solid var(--color-border);
    &:last-child {
        border-bottom: 0;
    }
`;
export const OperationHeader = styled(AccordionPrimitive.Header)`
    display: flex;
    min-width: 0;
    margin: 0;
`;
export const OperationTrigger = styled(AccordionPrimitive.Trigger)`
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    width: 100%;
    min-width: 0;
    gap: var(--space-3, 12px);
    padding: var(--space-3, 12px) var(--space-4, 16px);
    border: 0;
    color: var(--color-foreground);
    background: var(--color-card);
    font: inherit;
    text-align: left;
    cursor: pointer;
    &:hover {
        background: var(--color-muted);
    }
    &:focus-visible {
        outline: 2px solid var(--color-ring);
        outline-offset: -2px;
    }
    &[data-state='open'] svg {
        transform: rotate(180deg);
    }
`;
const methodTone = {
    get: css`
        color: var(--micro-app-reference-get-fg, #0550ae);
        background: var(--micro-app-reference-get-bg, #ddf4ff);
    `,
    post: css`
        color: var(--micro-app-reference-post-fg, #116329);
        background: var(--micro-app-reference-post-bg, #dafbe1);
    `,
    put: css`
        color: var(--micro-app-reference-put-fg, #8250df);
        background: var(--micro-app-reference-put-bg, #fbefff);
    `,
    patch: css`
        color: var(--micro-app-reference-patch-fg, #9a6700);
        background: var(--micro-app-reference-patch-bg, #fff8c5);
    `,
    delete: css`
        color: var(--micro-app-reference-delete-fg, #cf222e);
        background: var(--micro-app-reference-delete-bg, #ffebe9);
    `,
};
export const MethodBadge = styled.code<{ $method: EndpointMethod }>`
    display: inline-flex;
    min-width: 52px;
    justify-content: center;
    padding: var(--space-1, 4px) var(--space-2, 8px);
    border-radius: var(--radius-sm, 6px);
    font-size: var(--font-size-xs, 10px);
    font-weight: var(--font-weight-bold, 700);
    text-transform: uppercase;
    ${({ $method }) => methodTone[$method]}
`;
export const OperationIdentity = styled.span`
    display: flex;
    min-width: 0;
    flex: 1;
    flex-direction: column;
    gap: var(--space-1, 4px);
`;
export const OperationHeading = styled.span`
    display: flex;
    min-width: 0;
    align-items: baseline;
    gap: var(--space-3, 12px);

    @media (max-width: 720px) {
        align-items: flex-start;
        flex-direction: column;
        gap: var(--space-1, 4px);
    }
`;
export const OperationSummary = styled.strong`
    font-size: var(--font-size-sm, 12px);
`;
export const OperationPath = styled.code`
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    color: var(--color-muted-foreground);
    font-size: var(--font-size-xs, 10px);
    text-overflow: ellipsis;
    white-space: nowrap;
`;
export const OperationDescription = styled.p`
    margin: 0 0 var(--space-3, 12px);
    overflow-wrap: anywhere;
    color: var(--color-muted-foreground);
    font-size: var(--font-size-xs, 11px);
    line-height: 1.5;
`;
export const OperationAction = styled.div`
    display: flex;
    justify-content: flex-end;
    padding: var(--space-2, 8px) var(--space-3, 12px);
    border-top: 1px solid var(--color-border);
    background: var(--color-card);
`;
export const OperationChevron = styled.span`
    display: inline-flex;
    color: var(--color-muted-foreground);
    svg {
        width: 14px;
        height: 14px;
        transition: transform 200ms ease;
    }
`;
export const OperationContent = styled(AccordionPrimitive.Content)`
    overflow: hidden;
`;
export const ReferenceGrid = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1.08fr) minmax(340px, 0.92fr);
    border-top: 1px solid var(--color-border);
    @media (max-width: 900px) {
        grid-template-columns: minmax(0, 1fr);
    }
`;
export const DocumentationColumn = styled.div`
    min-width: 0;
    padding: var(--space-4, 16px);
`;
export const ExamplesColumn = styled.div`
    min-width: 0;
    padding: var(--space-4, 16px);
    border-left: 1px solid var(--color-border);
    background: var(--color-muted);
    @media (max-width: 900px) {
        border-top: 1px solid var(--color-border);
        border-left: 0;
    }
`;
export const DocumentationSection = styled.section`
    & + & {
        margin-top: var(--space-6, 24px);
    }
`;
export const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-3, 12px);
    margin-bottom: var(--space-2, 8px);
`;
export const SectionTitle = styled.h4`
    margin: 0;
    font-size: var(--font-size-xs, 11px);
    font-weight: var(--font-weight-semibold, 600);
`;
export const ContentType = styled.code`
    display: inline-flex;
    align-items: center;
    padding: 2px var(--space-2, 8px);
    border-radius: var(--radius-sm, 6px);
    color: var(--color-muted-foreground);
    background: var(--color-muted);
    font-family: var(--font-family-mono, monospace);
    font-size: var(--font-size-xs, 9px);
    font-weight: var(--font-weight-semibold, 600);
`;
export const FieldList = styled.ul`
    margin: 0;
    padding: 0;
    border-top: 1px solid var(--color-border);
    list-style: none;
`;
export const FieldRow = styled.li`
    padding: var(--space-3, 12px) 0;
    border-bottom: 1px solid var(--color-border);
`;
export const FieldIdentity = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: var(--space-2, 8px);
`;
export const FieldName = styled.code`
    font-size: var(--font-size-xs, 10px);
    font-weight: var(--font-weight-semibold, 600);
`;
export const FieldType = styled.span`
    color: var(--color-muted-foreground);
    font-size: var(--font-size-xs, 9px);
`;
export const FieldRequirement = styled.span<{ $required: boolean }>`
    color: ${({ $required }) =>
        $required
            ? 'var(--micro-app-reference-required-fg, #cf222e)'
            : 'var(--color-muted-foreground)'};
    font-size: var(--font-size-xs, 9px);
    font-weight: var(--font-weight-semibold, 600);
`;
export const FieldDescription = styled.p`
    margin: var(--space-1, 4px) 0 0;
    color: var(--color-muted-foreground);
    font-size: var(--font-size-xs, 10px);
    line-height: 1.5;
`;
export const FieldConstraints = styled.p`
    margin: var(--space-1, 4px) 0 0;
    color: var(--color-muted-foreground);
    font-family: var(--font-family-mono, monospace);
    font-size: var(--font-size-xs, 9px);
`;
export const FieldToggle = styled(CollapsiblePrimitive.Trigger)`
    display: inline-flex;
    align-items: center;
    gap: var(--space-1, 4px);
    margin-top: var(--space-2, 8px);
    border: 0;
    padding: 0;
    color: var(--color-primary);
    background: transparent;
    font: inherit;
    font-size: var(--font-size-xs, 10px);
    font-weight: var(--font-weight-semibold, 600);
    cursor: pointer;
    &:hover {
        text-decoration: underline;
    }
    &:focus-visible {
        outline: 2px solid var(--color-ring);
        outline-offset: 2px;
    }
    &[data-state='open'] svg {
        transform: rotate(180deg);
    }
    svg {
        width: 12px;
        height: 12px;
        flex-shrink: 0;
        transition: transform 200ms ease;
    }
`;
export const FieldChildren = styled(CollapsiblePrimitive.Content)`
    margin-left: var(--space-2, 8px);
    padding-left: var(--space-3, 12px);
    border-left: 1px solid var(--color-border);
`;
export const ResponseAccordion = styled.div`
    overflow: hidden;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md, 8px);
`;
export const ResponseTriggerContent = styled.span`
    display: grid;
    min-width: 0;
    flex: 1;
    grid-template-columns: 44px minmax(0, 1fr) auto;
    align-items: center;
    gap: var(--space-2, 8px);
    color: var(--color-muted-foreground);
    font-size: var(--font-size-xs, 9.5px);
    text-align: left;
`;
export const ResponseStatus = styled.code<{ $isSuccess: boolean }>`
    display: inline-flex;
    min-width: 38px;
    justify-content: center;
    padding: 2px var(--space-1, 4px);
    border-radius: var(--radius-sm, 6px);
    color: ${({ $isSuccess }) =>
        $isSuccess
            ? 'var(--micro-app-reference-success-fg, #116329)'
            : 'var(--micro-app-reference-error-fg, #cf222e)'};
    background: ${({ $isSuccess }) =>
        $isSuccess
            ? 'var(--micro-app-reference-success-bg, #dafbe1)'
            : 'var(--micro-app-reference-error-bg, #ffebe9)'};
    font-size: var(--font-size-xs, 10px);
    font-weight: var(--font-weight-bold, 700);
`;
export const ResponseOptionContent = styled.span`
    display: flex;
    min-width: 0;
    flex: 1;
    align-items: center;
    overflow: hidden;
    gap: var(--space-2, 8px);
`;
export const ResponseOptionDescription = styled.span`
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;
export const ResponseAccordionContent = styled(AccordionPrimitive.Content)`
    overflow: hidden;
`;
export const ResponseFields = styled.div`
    padding: 0 0 var(--space-3, 12px) var(--space-4, 16px);
`;
export const CodeCard = styled.section`
    min-width: 0;
    overflow: hidden;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md, 8px);
    background: var(--color-card);
    & + & {
        margin-top: var(--space-4, 16px);
    }
`;
export const CodeHeader = styled.div`
    display: flex;
    align-items: center;
    min-width: 0;
    overflow: hidden;
    min-height: 32px;
    padding: var(--space-1, 4px) var(--space-2, 8px);
    border-bottom: 1px solid var(--color-border);
    background: var(--color-card);
    font-size: var(--font-size-xs, 10px);
    font-weight: var(--font-weight-semibold, 600);
    > [role='combobox'] {
        width: 100%;
        min-width: 0;
        overflow: hidden;
        > span:first-child {
            min-width: 0;
            overflow: hidden;
            flex: 1;
        }
    }
`;
export const CodeHeaderIdentity = styled.span`
    display: flex;
    min-width: 0;
    flex: 1;
    align-items: center;
    overflow: hidden;
    gap: var(--space-2, 8px);
`;
export const CodeHeaderPath = styled.code`
    min-width: 0;
    overflow: hidden;
    color: var(--color-muted-foreground);
    font-size: var(--font-size-xs, 10px);
    text-overflow: ellipsis;
    white-space: nowrap;
`;
export const CodeContent = styled.div`
    max-width: 100%;
    overflow-x: auto;
    color: var(--color-foreground);
    background: var(--color-card);
    /*
     * Structural overrides only. Token colors come from the light Prism palette
     * (oneLight) that EndpointCodeExample passes as inline styles; overriding
     * them here would flatten the highlighting on the white card surface.
     */
    pre {
        margin: 0 !important;
        padding: var(--space-3, 12px) !important;
        background: transparent !important;
        font-size: var(--font-size-xs, 11px) !important;
        text-shadow: none !important;
    }
    code {
        background: transparent !important;
        font-family: var(--font-family-mono, monospace) !important;
        text-shadow: none !important;
    }
`;
export const EmptyResponseExample = styled.p`
    margin: 0;
    padding: var(--space-4, 16px);
    color: var(--color-muted-foreground);
    font-size: var(--font-size-xs, 11px);
`;
export const MissingOperation = styled.p`
    margin: 0;
    padding: var(--space-3, 12px) var(--space-4, 16px);
    color: var(--micro-app-reference-error-fg, #cf222e);
    font-size: var(--font-size-xs, 11px);
`;
